<template>
  <div class="robot-page">
    <div class="background-layer">
      <div class="glow glow-left"></div>
      <div class="glow glow-right"></div>
      <div class="grid"></div>
    </div>

    <div class="page-shell">
      <header class="page-header reveal-up">
        <div class="left-actions">
          <button class="back-btn" @click="goHome">返回首页</button>
          <button class="ghost-btn" @click="scrollToMyRobots">查看我已注册机器人</button>
        </div>
        <div>
          <p class="eyebrow">RoboCoin Robot Register</p>
          <h1>机器人注册</h1>
        </div>
      </header>

      <main class="robot-layout">
        <section class="robot-workspace">
          <aside class="robot-sidebar reveal-up">
            <section class="robot-summary-card">
              <div class="summary-head">
                <div>
                  <p class="eyebrow">Robot Summary</p>
                  <h2>Registration summary</h2>
                </div>
                <span>{{ completedReviewItems }}/{{ registerSummaryItems.length }}</span>
              </div>
              <div class="summary-list">
                <div v-for="item in registerSummaryItems" :key="item.key" class="summary-item">
                  <i :class="{ ready: item.ready }"></i>
                  <div>
                    <span>{{ item.label }}</span>
                    <strong>{{ item.value }}</strong>
                  </div>
                </div>
              </div>
            </section>
          </aside>
          <section class="form-card reveal-up delay-2">
            <div class="card-title-row">
              <div>
                <p class="eyebrow">Robot Wizard</p>
                <h3>按步骤注册机器人</h3>
              </div>
              <button class="ghost-btn" @click="resetForm">重置</button>
            </div>

            <div class="progress-overview">
              <span>Step {{ currentStep }} of {{ registerSteps.length }}</span>
              <strong>{{ activeQuestion.short }}</strong>
              <em>Robot profile</em>
            </div>

            <div class="register-progress">
              <button
                v-for="(step, index) in registerSteps"
                :key="step.key"
                type="button"
                :class="['flow-dot', { active: currentStep === index + 1, done: currentStep > index + 1, locked: index + 1 > maxVisitedStep && index + 1 > currentStep }]"
                :disabled="index + 1 > maxVisitedStep && index + 1 > currentStep"
                @click="goToStep(index + 1)"
              >
                <span>{{ index + 1 }}</span>
                <em>{{ step.short }}</em>
              </button>
            </div>

            <el-form ref="robotFormRef" :model="robotForm" :rules="robotRules" label-position="top" class="robot-form">
              <transition name="question-slide" mode="out-in">
                <section :key="activeQuestion.key" class="question-panel">
                  <div class="step-heading">
                    <span>Step {{ String(currentStep).padStart(2, '0') }}</span>
                    <h4>{{ activeQuestion.title }}</h4>
                    <p>{{ activeQuestion.help }}</p>
                  </div>
                  <div v-if="activeQuestion.key === 'name'" class="single-field">
                    <el-form-item label="机器人名称" prop="name">
                      <el-input
                        ref="nameInput"
                        v-model.trim="robotForm.name"
                        placeholder="例如：PIX 园区清扫 01"
                        @keyup.enter="nextStep"
                      ></el-input>
                    </el-form-item>
                  </div>

                  <el-form-item v-else-if="activeQuestion.key === 'category'" prop="category" class="choice-form-item">
                    <div class="category-choice-head">
                      <strong>{{ categories.length }} robot categories</strong>
                      <span>所有类别同时展示，可直接比较和切换。</span>
                    </div>
                    <div class="category-choice-grid" aria-label="Robot category selection">
                      <button
                        v-for="item in categories"
                        :key="item.value"
                        type="button"
                        :class="['choice-card', { selected: robotForm.category === item.value }]"
                        :aria-pressed="robotForm.category === item.value"
                        @click="selectCategory(item.value)"
                      >
                        <span>{{ item.short }}</span>
                        <strong>{{ item.label }}</strong>
                        <p>{{ item.text }}</p>
                        <small v-if="robotForm.category === item.value">已选择</small>
                      </button>
                    </div>
                  </el-form-item>

                  <div v-else-if="activeQuestion.key === 'serviceRegionLimit'" class="single-field">
                    <el-form-item label="服务区域（可选）" prop="serviceRegionLimit">
                      <el-input
                        v-model.trim="robotForm.serviceRegionLimit"
                        maxlength="255"
                        placeholder="例如：中国 / 贵州 / 贵阳；留空表示不限制服务区域"
                        @keyup.enter="nextStep"
                      ></el-input>
                    </el-form-item>
                    <div class="region-limit-actions">
                      <span>用于后续任务匹配和机器人市场展示。</span>
                      <button type="button" @click="setServiceRegionLimit('')">不限制服务区域</button>
                    </div>
                  </div>

                  <div v-else-if="activeQuestion.key === 'coverImageUrl'" class="single-field">
                    <el-form-item label="机器人照片" prop="coverImageUrl">
                      <el-upload
                        class="robot-upload"
                        action="/file/upload-local"
                        :limit="1"
                        :show-file-list="false"
                        :on-success="handleUploadSuccess"
                        :on-error="handleUploadError"
                        :before-upload="beforeUpload"
                        :http-request="uploadRobotImage"
                      >
                        <div v-if="robotForm.coverImageUrl" class="upload-preview">
                          <img :src="robotForm.coverImageUrl" alt="robot cover" />
                          <span>点击更换照片</span>
                        </div>
                        <div v-else class="upload-placeholder">
                          <i class="el-icon-plus"></i>
                          <span>上传一张机器人照片</span>
                        </div>
                      </el-upload>
                    </el-form-item>
                  </div>

                  <div v-else-if="activeQuestion.key === 'contactPhone'" class="single-field">
                    <el-form-item label="联系方式" prop="contactPhone">
                      <el-input
                        v-model.trim="robotForm.contactPhone"
                        placeholder="请输入手机号或其他联系方式"
                        @keyup.enter="nextStep"
                      ></el-input>
                    </el-form-item>
                    <el-form-item label="当前状态">
                      <el-input value="待验证通过" disabled></el-input>
                    </el-form-item>
                  </div>

                  <div v-else-if="activeQuestion.key === 'walAddress'" class="single-field">
                    <el-form-item label="机器人收款钱包地址（可选）" prop="walAddress">
                      <el-input
                        v-model.trim="robotForm.walAddress"
                        placeholder="例如：0xFaA0d9A670b6CC505EeC57046edD2FdB7A03AB55"
                        @keyup.enter="nextStep"
                      ></el-input>
                    </el-form-item>
                    <p class="wallet-defer-note">可暂时留空。机器人准备开启接单时，平台会要求补充有效的钱包地址。</p>
                    <div class="wallet-guide">
                      <div class="wallet-option">
                        <span>MetaMask</span>
                        <p>打开 MetaMask，复制 0x 开头的钱包地址后粘贴到这里。</p>
                      </div>
                      <div class="wallet-option">
                        <span>TokenPocket</span>
                        <p>打开 TokenPocket，进入收款页复制钱包地址。后续 RoboCoin 收益会发到这个地址。</p>
                      </div>
                    </div>
                  </div>

                  <div v-else-if="activeQuestion.key === 'capabilityDesc'" class="single-field">
                    <el-form-item label="能力简要描述（可选）">
                      <el-input
                        v-model.trim="robotForm.capabilityDesc"
                        type="textarea"
                        :rows="6"
                        placeholder="可选，简要描述机器人能力、适用场景或优势"
                      />
                    </el-form-item>
                  </div>

                  <div v-else class="final-review">
                    <div class="review-head">
                      <div>
                        <span>Final Check</span>
                        <h4>请最后确认机器人资料</h4>
                      </div>
                      <p>有信息不对，直接展开对应清单项修改；确认无误后提交注册。</p>
                      <div class="review-meter">
                        <strong>{{ completedReviewItems }}/{{ reviewItems.length }}</strong>
                        <em>已完成</em>
                      </div>
                    </div>

                    <div class="review-list">
                      <div
                        v-for="item in reviewItems"
                        :key="item.key"
                        :class="['review-item', { editing: reviewEditingKey === item.key, incomplete: !item.ready }]"
                      >
                        <div class="review-main">
                          <div class="review-index">{{ item.order }}</div>
                          <div class="review-copy">
                            <span>{{ item.label }}</span>
                            <strong>{{ item.value }}</strong>
                          </div>
                          <button type="button" @click="toggleReviewEdit(item.key)">
                            {{ reviewEditingKey === item.key ? '收起' : '修改' }}
                          </button>
                        </div>

                        <transition name="review-editor">
                          <div v-if="reviewEditingKey === item.key" class="review-editor">
                            <div v-if="item.key === 'name'" class="review-field-grid">
                              <el-form-item label="机器人名称" prop="name">
                                <el-input v-model.trim="robotForm.name" placeholder="例如：PIX 园区清扫 01" />
                              </el-form-item>
                            </div>

                            <div v-else-if="item.key === 'category'" class="review-choice-row">
                              <button
                                v-for="option in categories"
                                :key="option.value"
                                type="button"
                                :class="['mini-choice', { selected: robotForm.category === option.value }]"
                                @click="setCategory(option.value)"
                              >
                                <span>{{ option.short }}</span>
                                <strong>{{ option.label }}</strong>
                              </button>
                            </div>

                            <div v-else-if="item.key === 'serviceRegionLimit'" class="review-field-grid">
                              <el-form-item label="服务区域（可选）">
                                <el-input
                                  v-model.trim="robotForm.serviceRegionLimit"
                                  maxlength="255"
                                  placeholder="例如：中国 / 贵州 / 贵阳；留空表示不限制"
                                />
                              </el-form-item>
                            </div>

                            <div v-else-if="item.key === 'coverImageUrl'" class="review-field-grid">
                              <el-form-item label="机器人照片" prop="coverImageUrl">
                                <el-upload
                                  class="robot-upload"
                                  action="/file/upload-local"
                                  :limit="1"
                                  :show-file-list="false"
                                  :on-success="handleUploadSuccess"
                                  :on-error="handleUploadError"
                                  :before-upload="beforeUpload"
                                  :http-request="uploadRobotImage"
                                >
                                  <div v-if="robotForm.coverImageUrl" class="upload-preview compact-preview">
                                    <img :src="robotForm.coverImageUrl" alt="robot cover" />
                                    <span>点击更换照片</span>
                                  </div>
                                  <div v-else class="upload-placeholder compact-preview">
                                    <i class="el-icon-plus"></i>
                                    <span>上传一张机器人照片</span>
                                  </div>
                                </el-upload>
                              </el-form-item>
                            </div>

                            <div v-else-if="item.key === 'contactPhone'" class="review-field-grid">
                              <el-form-item label="联系方式" prop="contactPhone">
                                <el-input v-model.trim="robotForm.contactPhone" placeholder="请输入手机号或其他联系方式" />
                              </el-form-item>
                            </div>

                            <div v-else-if="item.key === 'walAddress'" class="review-field-grid">
                              <el-form-item label="机器人收款钱包地址（可选）" prop="walAddress">
                                <el-input v-model.trim="robotForm.walAddress" placeholder="例如：0xFaA0d9A670b6CC505EeC57046edD2FdB7A03AB55" />
                              </el-form-item>
                            </div>

                            <div v-else-if="item.key === 'capabilityDesc'" class="review-field-grid">
                              <el-form-item label="能力简要描述（可选）">
                                <el-input
                                  v-model.trim="robotForm.capabilityDesc"
                                  type="textarea"
                                  :rows="4"
                                  placeholder="可选，简要描述机器人能力、适用场景或优势"
                                />
                              </el-form-item>
                            </div>

                            <div class="review-editor-actions">
                              <button class="review-done-btn" type="button" @click="finishReviewEdit(item)">完成修改</button>
                              <button class="review-step-btn" type="button" @click="jumpToStepFromReview(item.step)">回到这一步</button>
                            </div>
                          </div>
                        </transition>
                      </div>
                    </div>
                  </div>
                </section>
              </transition>
            </el-form>

            <div class="wizard-actions">
              <button class="ghost-btn" type="button" @click="prevStep" :disabled="currentStep === 1">上一步</button>
              <button v-if="!isReviewStep" class="primary-btn" type="button" @click="nextStep">下一步</button>
              <button v-else class="primary-btn" type="button" @click="submitRegister">确认注册</button>
            </div>
          </section>
        </section>

        <section ref="myRobotsSection" class="list-card reveal-up delay-3">
          <div class="card-title-row">
            <div>
              <p class="eyebrow">My Robots</p>
              <h3>我已注册的机器人</h3>
            </div>
            <div class="robot-list-actions">
              <button class="back-btn" type="button" @click="goHome">返回首页</button>
              <button class="ghost-btn" type="button" @click="loadMyRobots">刷新列表</button>
            </div>
          </div>

          <div v-if="myRobots.length" class="robot-grid">
            <article
              v-for="robot in myRobots"
              :key="robot.id"
              :class="['robot-card', { 'robot-card-cancelled': robot.registerStatus === 'CANCELLED' }]"
            >
              <div class="robot-cover">
                <img :src="robot.coverImageUrl" :alt="robot.name" />
                <div class="robot-cover-overlay">
                  <el-tag type="warning">{{ robot.registerStatus === 'PENDING_REVIEW' ? '待验证通过' : robot.registerStatus }}</el-tag>
                </div>
                <div v-if="robot.registerStatus === 'CANCELLED'" class="cancel-ribbon">已取消注册</div>
              </div>
              <div class="robot-card-body">
                <div class="robot-card-head">
                  <h4>{{ robot.name }}</h4>
                  <span>{{ categoryLabel(robot.category) }}</span>
                </div>
                <p class="robot-contact">联系方式：{{ robot.contactPhone }}</p>
                <p class="robot-wallet">收款钱包：{{ shortenWalletAddress(robot.walAddress) }}</p>
                <div class="dispatch-toggle-line" :class="{ accepting: robot.acceptingOrders !== 0 }">
                  <div>
                    <strong>{{ robot.acceptingOrders !== 0 ? '接单中' : '暂停接单' }}</strong>
                    <span>{{ robot.acceptingOrders !== 0 ? 'DeRAS可向该机器人发送派单邀请' : '不会收到新的自动派单邀请' }}</span>
                  </div>
                  <button
                    type="button"
                    :disabled="robot.registerStatus !== 'APPROVED'"
                    @click="toggleRobotAccepting(robot)"
                  >
                    {{ robot.acceptingOrders !== 0 ? '暂停' : '开启' }}
                  </button>
                </div>
                <div class="robot-agent-line" :class="{ online: robot.sdkAgentOnline, offline: !robot.sdkAgentOnline }">
                  <span></span>
                  <strong>{{ robot.sdkAgentOnline ? 'SDK Agent 在线' : 'SDK Agent 离线' }}</strong>
                  <em>{{ heartbeatText(robot) }}</em>
                </div>
                <div class="device-check-card">
                  <div class="device-check-head">
                    <strong>设备上线检测</strong>
                    <em>{{ deviceCheckSummary(robot) }}</em>
                  </div>
                  <div class="device-check-steps">
                    <div
                      v-for="step in deviceCheckSteps(robot)"
                      :key="step.key"
                      :class="['device-check-step', step.status]"
                    >
                      <span>
                        <i v-if="step.status === 'done'" class="el-icon-check"></i>
                        <i v-else-if="step.status === 'current'" class="el-icon-loading"></i>
                        <i v-else class="el-icon-minus"></i>
                      </span>
                      <div>
                        <strong>{{ step.title }}</strong>
                        <small>{{ step.text }}</small>
                      </div>
                    </div>
                  </div>
                </div>
                <p class="robot-desc">{{ robot.capabilityDesc || '未填写能力简要描述。' }}</p>
                <p class="robot-note">{{ robot.verificationNote || '待验证通过' }}</p>
                <p v-if="robot.sdkBoundAt && !robot.sdkConfirmedAt" class="robot-note warning">
                  SDK已上报，请确认这是不是你的设备。
                </p>
                <div v-if="robot.sdkBindingToken" class="robot-token-line">
                  <span>SDK绑定令牌：{{ shortenToken(robot.sdkBindingToken) }}</span>
                  <button type="button" @click.stop="copyText(robot.sdkBindingToken, '绑定令牌已复制')">复制令牌</button>
                </div>
                <div class="robot-sdk-actions">
                  <button class="sdk-mini-btn archive" type="button" @click="openRobotArchive(robot)">查看机器人档案</button>
                  <button class="sdk-mini-btn wallet" type="button" @click="openWalletSetup(robot)">
                    {{ isWalletAddress(robot.walAddress) ? '更新收款钱包' : '填写收款钱包' }}
                  </button>
                  <button class="sdk-mini-btn" type="button" @click="openSdkGuide(robot)">查看 SDK 安装步骤</button>
                  <button
                    v-if="robot.sdkBoundAt && !robot.sdkConfirmedAt"
                    class="sdk-mini-btn confirm"
                    type="button"
                    @click="confirmDevice(robot)"
                  >
                    确认是我的设备
                  </button>
                </div>
                <button
                  class="cancel-btn"
                  type="button"
                  @click="cancelRobot(robot)"
                  :disabled="robot.registerStatus === 'CANCELLED'"
                >
                  {{ robot.registerStatus === 'CANCELLED' ? '已取消注册' : '取消注册' }}
                </button>
              </div>
            </article>
          </div>

          <div v-else class="empty-state">
            暂无已注册机器人，先提交第一台机器人资料吧。
          </div>
        </section>
      </main>
    </div>

    <el-dialog
      :model-value="submitDialogVisible"
      @update:model-value="submitDialogVisible = $event"
      width="360px"
      custom-class="process-dialog"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      append-to-body
    >
      <div class="process-content">
        <i :class="submitDialogDone ? 'el-icon-circle-check process-success' : 'el-icon-loading process-loading'"></i>
        <h3>{{ submitDialogTitle }}</h3>
        <p>{{ submitDialogText }}</p>
      </div>
    </el-dialog>

    <el-dialog
      :model-value="sdkGuideVisible"
      @update:model-value="sdkGuideVisible = $event"
      width="860px"
      custom-class="sdk-dialog"
      append-to-body
      @closed="handleSdkGuideClosed"
    >
        <div class="sdk-dialog-content">
          <div class="sdk-dialog-hero">
            <div>
              <p class="eyebrow">SDK SETUP AFTER REGISTRATION</p>
              <h3>机器人注册成功</h3>
              <p>在机器人工控机终端执行安装命令，页面会自动识别设备状态。</p>
            </div>
            <div class="dialog-chip">SDK</div>
          </div>

        <div class="selected-robot" v-if="selectedSdkRobot">
          <span>当前机器人</span>
          <strong>{{ selectedSdkRobot.name }}</strong>
          <em>ID: {{ selectedSdkRobot.id }}</em>
          <em>绑定令牌：{{ shortenToken(selectedSdkRobot.sdkBindingToken) }}</em>
          <em>有效期：{{ selectedSdkRobot.sdkBindingTokenExpiresAt || '已失效或已绑定' }}</em>
        </div>

        <div class="sdk-device-check" v-if="sdkDetailsVisible && selectedSdkRobot">
          <div class="sdk-device-check-title">
            <div>
              <p class="eyebrow">DEVICE ONLINE CHECK</p>
              <h4>设备上线检测</h4>
            </div>
            <strong>{{ deviceCheckSummary(selectedSdkRobot) }}</strong>
          </div>
          <div class="device-check-steps dialog">
            <div
              v-for="step in deviceCheckSteps(selectedSdkRobot)"
              :key="step.key"
              :class="['device-check-step', step.status]"
            >
              <span>
                <i v-if="step.status === 'done'" class="el-icon-check"></i>
                <i v-else-if="step.status === 'current'" class="el-icon-loading"></i>
                <i v-else class="el-icon-minus"></i>
              </span>
              <div>
                <strong>{{ step.title }}</strong>
                <small>{{ step.text }}</small>
              </div>
            </div>
          </div>
        </div>

        <div v-if="sdkDetailsVisible && sdkInstallSlowHintVisible && selectedSdkRobot" class="sdk-slow-hint">
          <i class="el-icon-warning-outline"></i>
          <div>
            <strong>页面还没收到完整在线结果</strong>
            <p>{{ sdkSlowHintText(selectedSdkRobot) }}</p>
          </div>
        </div>

        <div v-if="sdkDetailsVisible" class="install-steps">
          <div v-for="step in sdkSteps" :key="step.index" class="install-step">
            <span>{{ step.index }}</span>
            <div>
              <strong>{{ step.title }}</strong>
              <p>{{ step.text }}</p>
            </div>
          </div>
        </div>

        <div v-if="sdkDetailsVisible" class="sdk-user-note">
          <i class="el-icon-lock"></i>
          <div>
            <strong>你只需要完成安装，设备认证由 SDK 自动处理</strong>
            <p>SDK 会在工控机本地读取芯片身份并发起安全签名，私钥不会离开芯片。绑定成功后，同一个机器人 ID 再换其他工控机上报会被平台拦截。</p>
          </div>
        </div>

        <div class="command-panel">
          <div class="command-block">
            <span>在工控机终端执行这一条命令，自动安装、绑定并启动后台服务</span>
            <code>{{ sdkInstallCommand }}</code>
          </div>
          <button class="copy-btn" type="button" @click="copyText(sdkInstallCommand, '一键安装命令已复制')">复制一键安装命令</button>
        </div>

        <div class="command-panel">
          <div class="command-block">
            <span>故障排查：正常注册不需要执行，只有页面一直离线时由管理员指导使用</span>
            <code>{{ sdkDoctorCommand }}</code>
          </div>
          <button class="copy-btn" type="button" @click="copyText(sdkDoctorCommand, 'SDK排查命令已复制')">复制排查命令</button>
        </div>

        <div v-if="sdkDetailsVisible" class="sdk-output-note">
          <strong>看到什么算完成？</strong>
          <p>终端里出现 <code>signatureVerified=true</code> 和 <code>reportId</code>，说明 TPM 验签通过；此时令牌会立即失效。</p>
          <p>页面会自动刷新设备状态。如果看到“SDK已上报”，请确认这是不是你的设备，确认后机器人正式通过。</p>
          <p>一键命令绑定成功后会创建 <code>roc-robot-agent</code> 后台服务，并每 30 秒向 DeRAS 上报一次 TPM 签名心跳。后续服务者点击出发、申请开始或申请结束时，SDK 会自动向 DeRAS 上报。</p>
          <p>安装命令最后会自动完成一次诊断，正常情况下用户不需要再执行其他命令。工控机重启后服务会自动恢复。</p>
          <p>如果页面长时间显示离线，再由管理员指导执行 <code>cd ~/roc-robot-sdk && ./roc-robot-tpm-sdk.sh doctor</code>，它会检查服务状态、TPM权限、后端连接、绑定结果和最近心跳。</p>
          <p>如果超过10分钟、网络断开或工控机关机，请点击“重新生成绑定令牌”，再复制新的一键安装命令。</p>
        </div>

        <div v-if="sdkDetailsVisible && selectedSdkRobot && selectedSdkRobot.sdkBoundAt && !selectedSdkRobot.sdkConfirmedAt" class="sdk-confirm-box">
          <div>
            <strong>SDK 已经读取到设备</strong>
            <p>请确认这台设备确实属于当前机器人。确认后，平台会把这个 TPM 公钥作为后续 DeRAS 派单验签依据。</p>
          </div>
          <button class="primary-btn" type="button" @click="confirmDevice(selectedSdkRobot)">确认这是我的设备</button>
        </div>

        <div class="sdk-dialog-actions">
          <button class="ghost-btn" type="button" @click="sdkDetailsVisible = !sdkDetailsVisible">{{ sdkDetailsVisible ? '收起详情' : '查看详情' }}</button>
          <button v-if="sdkDetailsVisible" class="ghost-btn" type="button" @click="regenerateToken">重新生成令牌</button>
          <button class="ghost-btn" type="button" @click="sdkGuideVisible = false">我知道了</button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  cancelRegisteredRobot,
  confirmSdkDevice,
  getMyRegisteredRobots,
  regenerateSdkBindingToken,
  registerRobotProfile,
  updateRobotAcceptingOrders
} from '../api/robot-register'
import { getAuthUser } from '../utils/auth'

export default {
  name: 'RobotRegisterPage',
  data() {
    return {
      currentUser: getAuthUser(),
      myRobots: [],
      submitDialogVisible: false,
      submitDialogDone: false,
      submitDialogTitle: '',
      submitDialogText: '',
      sdkGuideVisible: false,
      sdkDetailsVisible: false,
      selectedSdkRobot: null,
      lastRegisteredRobot: null,
      currentStep: 1,
      maxVisitedStep: 1,
      autoAdvanceTimer: null,
      reviewEditingKey: '',
      sdkStatusPollTimer: null,
      sdkStatusPollStartedAt: null,
      sdkLastStatusKey: '',
      sdkInstallSlowHintVisible: false,
      sdkGitHubRawInstallUrl: process.env.VUE_APP_ROC_SDK_INSTALL_URL || 'https://raw.githubusercontent.com/robocoin-service/roc-robot-sdk/main/install.sh',
      sdkSteps: [
        {
          index: '01',
          title: '打开机器人上的工控机终端',
          text: '不需要先在 Windows 下载，也不需要手动上传 zip。直接 SSH 登录工控机，在工控机终端里执行页面给出的命令。'
        },
        {
          index: '02',
          title: '复制专属绑定令牌命令',
          text: '每台机器人注册后都会生成一次性绑定令牌，命令会带上这个令牌，平台就知道这次上报属于哪一个机器人。'
        },
        {
          index: '03',
          title: 'SDK 自动拉取并绑定 TPM',
          text: '命令会从 GitHub 拉取 SDK，检查 TPM 工具，读取 TPM 公钥，让 TPM 私钥签名后上报平台数据库。'
        },
        {
          index: '04',
          title: '自动启动后台服务',
          text: '绑定成功后 SDK 会创建 roc-robot-agent 服务，开机自启，每30秒发送一次TPM签名心跳；后续订单出发、开始和结束验证都由它自动签名上报。'
        }
      ],
      categories: [
        { value: 'WHEELED', label: '轮式机器人', short: '轮', text: '适合移动底盘、园区巡航、通用移动平台。' },
        { value: 'CLEANING', label: '清洁机器人', short: '扫', text: '适合扫地、保洁、地面清扫等场景。' },
        { value: 'DRONE', label: '无人机', short: '飞', text: '适合空中巡航、拍摄、轻量配送。' },
        { value: 'SHUTTLE', label: '接驳机器人', short: '驳', text: '适合人员或货物短途接驳。' },
        { value: 'INSPECTION', label: '巡检机器人', short: '巡', text: '适合巡逻、检查、异常发现。' },
        { value: 'GO2', label: '机器狗 Go2', short: '狗', text: '适合跳舞、转身、短距离移动等演示任务。' }
      ],
      robotForm: this.createEmptyForm(),
      robotRules: {
        name: [{ required: true, message: '请输入机器人名称', trigger: 'blur' }],
        category: [{ required: true, message: '请选择机器人类别', trigger: 'change' }],
        coverImageUrl: [{ required: true, message: '请上传机器人照片', trigger: 'change' }],
        contactPhone: [{ required: true, message: '请输入联系方式', trigger: 'blur' }],
        walAddress: [{ validator: this.validateWalletAddress, trigger: 'blur' }]
      }
    }
  },
  created() {
    this.loadMyRobots()
  },
  beforeDestroy() {
    if (this.autoAdvanceTimer) {
      window.clearTimeout(this.autoAdvanceTimer)
    }
    this.stopSdkStatusPolling()
  },
  computed: {
    registerSteps() {
      return [
        {
          key: 'name',
          short: 'Name',
          title: 'Name your robot',
          help: 'Use a clear name that users can recognize in the robot market.'
        },
        {
          key: 'category',
          short: 'Category',
          title: 'Choose its robot category',
          help: 'Select the primary type so the platform can match suitable missions.'
        },
        {
          key: 'serviceRegionLimit',
          short: 'Region',
          title: 'Set its service area',
          help: 'Optional. Leave this empty when the robot can serve without a regional restriction.'
        },
        {
          key: 'coverImageUrl',
          short: 'Photo',
          title: 'Add a robot photo',
          help: 'Upload one clear image for the profile and robot market.'
        },
        {
          key: 'contactPhone',
          short: 'Contact',
          title: 'Add a contact method',
          help: 'This is used only for registration and task coordination.'
        },
        {
          key: 'walAddress',
          short: 'Wallet',
          title: 'Set the receiving wallet',
          help: 'Optional now. A valid EVM wallet is required before this robot can accept orders.'
        },
        {
          key: 'capabilityDesc',
          short: 'Capability',
          title: 'Describe its capabilities',
          help: 'Optional. Add the tasks and environments this robot handles well.'
        },
        {
          key: 'review',
          short: 'Review',
          title: 'Review and register',
          help: 'Confirm the profile before creating the robot record.'
        }
      ]
    },
    activeQuestion() {
      return this.registerSteps[this.currentStep - 1] || this.registerSteps[0]
    },
    isReviewStep() {
      return this.activeQuestion.key === 'review'
    },
    registerSummaryItems() {
      return this.reviewItems.filter(item => item.key !== 'capabilityDesc')
    },
    completedReviewItems() {
      return this.reviewItems.filter(item => item.ready).length
    },
    reviewItems() {
      return [
        { key: 'name', order: '01', label: '机器人名称', value: this.robotForm.name || '未填写', step: 1, ready: !!this.robotForm.name },
        { key: 'category', order: '02', label: '机器人类别', value: this.categoryLabel(this.robotForm.category), step: 2, ready: !!this.robotForm.category },
        { key: 'serviceRegionLimit', order: '03', label: '服务区域', value: this.robotForm.serviceRegionLimit || '不限制服务区域', step: 3, ready: true },
        { key: 'coverImageUrl', order: '04', label: '机器人照片', value: this.robotForm.coverImageUrl ? '已上传照片' : '未上传', step: 4, ready: !!this.robotForm.coverImageUrl },
        { key: 'contactPhone', order: '05', label: '联系方式', value: this.robotForm.contactPhone || '未填写', step: 5, ready: !!this.robotForm.contactPhone },
        { key: 'walAddress', order: '06', label: '收款钱包', value: this.robotForm.walAddress || '暂未填写，接单前补充', step: 6, ready: !this.robotForm.walAddress || this.isWalletAddress(this.robotForm.walAddress) },
        { key: 'capabilityDesc', order: '07', label: '能力描述', value: this.robotForm.capabilityDesc || '未填写，提交时会保存为空', step: 7, ready: true }
      ]
    },
    sdkRobotId() {
      const robotId = this.selectedSdkRobot && this.selectedSdkRobot.id ? this.selectedSdkRobot.id : '<机器人ID>'
      return robotId
    },
    sdkOwnerUserId() {
      const ownerUserId = this.currentUser && this.currentUser.id ? this.currentUser.id : '<用户ID>'
      return ownerUserId
    },
    sdkBindingToken() {
      const token = this.selectedSdkRobot && this.selectedSdkRobot.sdkBindingToken ? this.selectedSdkRobot.sdkBindingToken : '<SDK绑定令牌>'
      return token
    },
    sdkInstallCommand() {
      return `curl -fsSL ${this.sdkGitHubRawInstallUrl} | bash -s -- ${this.sdkBindingToken}`
    },
    sdkAgentCommand() {
      return `cd ~/roc-robot-sdk && ./roc-robot-tpm-sdk.sh agent ${this.sdkRobotId}`
    },
    sdkServiceStatusCommand() {
      return 'sudo systemctl status roc-robot-agent'
    },
    sdkServiceRestartCommand() {
      return 'sudo systemctl restart roc-robot-agent'
    },
    sdkServiceLogCommand() {
      return 'sudo journalctl -u roc-robot-agent -f'
    },
    sdkDoctorCommand() {
      return 'cd ~/roc-robot-sdk && ./roc-robot-tpm-sdk.sh doctor'
    }
  },
  methods: {
    createEmptyForm() {
      return {
        name: '',
        category: '',
        coverImagePath: '',
        coverImageUrl: '',
        contactPhone: '',
        serviceRegionLimit: '',
        walAddress: '',
        capabilityDesc: ''
      }
    },
    goHome() {
      window.location.href = '/#/home'
      window.setTimeout(() => window.location.reload(), 80)
    },
    scrollToMyRobots() {
      this.$refs.myRobotsSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    openSdkGuide(robot) {
      this.selectedSdkRobot = robot || this.lastRegisteredRobot || this.myRobots[0] || null
      this.sdkDetailsVisible = false
      this.sdkGuideVisible = true
      this.$nextTick(() => {
        try {
          this.startSdkStatusPolling()
        } catch (error) {
          console.error('SDK status polling failed:', error)
        }
      })
    },
    openRobotArchive(robot) {
      if (!robot || !robot.id) return
      this.$router.push({ name: 'robotArchive', params: { robotId: robot.id } })
    },
    openWalletSetup(robot) {
      if (!robot || !robot.id) return
      this.$router.push({ name: 'robotArchive', params: { robotId: robot.id }, query: { focus: 'wallet' } })
    },
    handleSdkGuideClosed() {
      this.stopSdkStatusPolling()
    },
    prepareSdkForRobot(robot) {
      this.selectedSdkRobot = robot || null
    },
    startSdkStatusPolling() {
      this.stopSdkStatusPolling()
      this.sdkStatusPollStartedAt = Date.now()
      this.sdkLastStatusKey = this.sdkProgressKey(this.selectedSdkRobot)
      this.sdkInstallSlowHintVisible = false
      this.refreshSelectedSdkRobot({ silent: true })
      this.sdkStatusPollTimer = window.setInterval(() => {
        this.refreshSelectedSdkRobot({ silent: false })
      }, 5000)
    },
    stopSdkStatusPolling() {
      if (this.sdkStatusPollTimer) {
        window.clearInterval(this.sdkStatusPollTimer)
        this.sdkStatusPollTimer = null
      }
    },
    async refreshSelectedSdkRobot({ silent = false } = {}) {
      if (!this.sdkGuideVisible || !this.selectedSdkRobot || !this.currentUser || !this.currentUser.id) return

      try {
        const response = await getMyRegisteredRobots(this.currentUser.id)
        if (response.data.code !== 200) {
          if (!silent) this.$message.warning(response.data.msg || '设备状态刷新失败')
          return
        }

        const robots = response.data.data || []
        this.myRobots = robots.filter(robot => robot.registerStatus !== 'CANCELLED')
        const freshRobot = robots.find(item => String(item.id) === String(this.selectedSdkRobot.id))
        if (!freshRobot) return

        const previousKey = this.sdkLastStatusKey || this.sdkProgressKey(this.selectedSdkRobot)
        this.selectedSdkRobot = freshRobot
        const nextKey = this.sdkProgressKey(freshRobot)
        this.sdkInstallSlowHintVisible = this.shouldShowSdkSlowHint(freshRobot)

        if (!silent && nextKey !== previousKey) {
          this.notifySdkProgress(nextKey)
        }
        this.sdkLastStatusKey = nextKey
      } catch (error) {
        if (!silent) this.$message.warning('设备状态刷新失败，请确认后端服务是否在线')
      }
    },
    async toggleRobotAccepting(robot) {
      if (!robot || !this.currentUser || !this.currentUser.id) return
      const nextValue = robot.acceptingOrders !== 0 ? 0 : 1
      try {
        const response = await updateRobotAcceptingOrders(robot.id, {
          userId: this.currentUser.id,
          acceptingOrders: nextValue
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '接单状态更新失败')
          return
        }
        robot.acceptingOrders = nextValue
        this.$message.success(nextValue === 1 ? '已开启接单，DeRAS可以派单' : '已暂停接单')
        this.loadMyRobots()
      } catch (error) {
        this.$message.error('接单状态更新失败，请检查后端服务')
      }
    },
    sdkProgressKey(robot) {
      if (!robot) return 'NO_ROBOT'
      if (robot.sdkLastHeartbeatVerified === false) return 'HEARTBEAT_FAILED'
      if (robot.sdkAgentOnline) return 'ONLINE'
      if (robot.sdkConfirmedAt) return 'CONFIRMED_WAITING_HEARTBEAT'
      if (robot.sdkBoundAt) return 'BOUND_PENDING_CONFIRM'
      return 'WAITING_INSTALL'
    },
    notifySdkProgress(statusKey) {
      const messages = {
        BOUND_PENDING_CONFIRM: { type: 'success', text: '已收到 TPM 公钥，请确认这是不是你的设备' },
        CONFIRMED_WAITING_HEARTBEAT: { type: 'success', text: '设备已确认，正在等待 SDK Agent 心跳' },
        ONLINE: { type: 'success', text: 'SDK Agent 已在线，后续验签会自动上报' },
        HEARTBEAT_FAILED: { type: 'warning', text: '最近一次心跳验签异常，请联系管理员排查' }
      }
      const item = messages[statusKey]
      if (!item) return
      this.$message[item.type](item.text)
    },
    shouldShowSdkSlowHint(robot) {
      if (!this.sdkStatusPollStartedAt || !robot) return false
      if (robot.sdkAgentOnline) return false
      const elapsed = Date.now() - this.sdkStatusPollStartedAt
      return elapsed > 120000
    },
    sdkSlowHintText(robot) {
      if (!robot || !robot.sdkBoundAt) {
        return '如果工控机终端还在执行命令，请等它跑完；如果已经报错，请把终端输出发给管理员。'
      }
      if (!robot.sdkConfirmedAt) {
        return '平台已收到 TPM 公钥，请先点击“确认这是我的设备”。'
      }
      return '设备已确认但 Agent 还未在线，请检查工控机是否开机、网络是否能访问平台。管理员可使用排查命令进一步定位。'
    },
    async copyText(text, successMessage = '内容已复制') {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          const input = document.createElement('textarea')
          input.value = text
          document.body.appendChild(input)
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
        }
        this.$message.success(successMessage)
      } catch (error) {
        this.$message.warning('复制失败，请手动复制')
      }
    },
    normalizeWalletAddress(value) {
      return String(value || '').replace(/\s+/g, '').trim()
    },
    isWalletAddress(value) {
      return /^0x[a-fA-F0-9]{40}$/.test(this.normalizeWalletAddress(value))
    },
    validateWalletAddress(rule, value, callback) {
      const normalized = this.normalizeWalletAddress(value)
      if (!normalized) {
        callback()
        return
      }
      if (normalized !== value) {
        this.robotForm.walAddress = normalized
      }
      if (!this.isWalletAddress(normalized)) {
        callback(new Error('钱包地址格式不正确，应为 0x 开头且后接 40 位十六进制字符'))
        return
      }
      callback()
    },
    validationMessage(fields) {
      for (const field of fields) {
        if (field === 'name' && !this.robotForm.name) return '请填写机器人名称'
        if (field === 'category' && !this.robotForm.category) return '请选择机器人类别'
        if (field === 'coverImageUrl' && !this.robotForm.coverImageUrl) return '请上传机器人照片'
        if (field === 'contactPhone' && !this.robotForm.contactPhone) return '请填写联系方式'
        if (field === 'walAddress') {
          return this.normalizeWalletAddress(this.robotForm.walAddress)
            ? '收款钱包格式不正确，应为 0x 开头的 42 位地址'
            : ''
        }
      }
      return '请先完成当前步骤的必填内容'
    },
    shortenWalletAddress(value) {
      if (!value) return '未填写'
      const text = String(value)
      return text.length > 14 ? `${text.slice(0, 8)}...${text.slice(-6)}` : text
    },
    shortenToken(value) {
      if (!value) return '待生成'
      const text = String(value)
      return text.length > 18 ? `${text.slice(0, 10)}...${text.slice(-8)}` : text
    },
    heartbeatText(robot) {
      if (!robot || !robot.sdkLastHeartbeatAt) {
        return robot && robot.sdkConfirmedAt ? '等待心跳上报' : '设备确认后开始检测'
      }
      const age = Number(robot.sdkHeartbeatAgeSeconds)
      const ageText = Number.isFinite(age) ? this.formatHeartbeatAge(age) : robot.sdkLastHeartbeatAt
      if (robot.sdkAgentOnline) {
        return `${ageText}前心跳验签通过`
      }
      if (robot.sdkLastHeartbeatVerified === false) {
        return robot.sdkLastHeartbeatMessage || '最近心跳验签失败'
      }
      return `${ageText}前最后一次心跳`
    },
    deviceCheckSummary(robot) {
      if (!robot) return '等待机器人资料'
      if (robot.sdkAgentOnline) return '设备在线，心跳正常'
      if (robot.sdkLastHeartbeatVerified === false) return '最近心跳验签异常'
      if (robot.sdkConfirmedAt) return '设备已确认，等待 Agent 心跳'
      if (robot.sdkBoundAt) return '已收到 TPM 公钥，等待确认'
      return '等待 SDK 安装'
    },
    deviceCheckSteps(robot) {
      const hasBound = Boolean(robot && robot.sdkBoundAt)
      const hasConfirmed = Boolean(robot && robot.sdkConfirmedAt)
      const isOnline = Boolean(robot && robot.sdkAgentOnline)
      const hasHeartbeat = Boolean(robot && robot.sdkLastHeartbeatAt)
      const heartbeatFailed = Boolean(robot && robot.sdkLastHeartbeatVerified === false)
      const steps = [
        {
          key: 'install',
          title: '等待 SDK 安装',
          text: hasBound ? 'SDK 已在工控机完成首次上报。' : '在工控机终端执行一行安装命令。',
          done: hasBound
        },
        {
          key: 'publicKey',
          title: '已收到 TPM 公钥',
          text: hasBound ? `公钥指纹：${this.shortFingerprint(robot.tpmPublicKeyFingerprint || robot.sdkLastHeartbeatFingerprint)}` : '平台还没有收到 TPM 公钥和签名报告。',
          done: hasBound
        },
        {
          key: 'confirm',
          title: '等待用户确认设备',
          text: hasConfirmed ? '用户已确认该 TPM 设备属于当前机器人。' : (hasBound ? '请确认这是不是你的设备。' : '收到 TPM 公钥后再确认设备。'),
          done: hasConfirmed
        },
        {
          key: 'agent',
          title: 'SDK Agent 在线',
          text: isOnline ? '2分钟内收到 TPM 签名心跳。' : (hasConfirmed ? '等待 Agent 心跳上报。' : '确认设备后开始检测 Agent。'),
          done: isOnline,
          failed: heartbeatFailed
        },
        {
          key: 'heartbeat',
          title: '最近心跳时间',
          text: hasHeartbeat ? this.heartbeatText(robot) : '暂无心跳记录。',
          done: isOnline,
          failed: heartbeatFailed
        }
      ]
      return steps.map((step, index) => {
        let status = 'todo'
        if (step.failed) {
          status = 'failed'
        } else if (step.done) {
          status = 'done'
        } else {
          const previousDone = steps.slice(0, index).every(item => item.done)
          status = previousDone ? 'current' : 'todo'
        }
        return { ...step, status }
      })
    },
    formatHeartbeatAge(seconds) {
      const value = Math.max(0, Math.floor(seconds))
      if (value < 60) return `${value}秒`
      const minutes = Math.floor(value / 60)
      if (minutes < 60) return `${minutes}分钟`
      const hours = Math.floor(minutes / 60)
      return `${hours}小时`
    },
    shortFingerprint(value) {
      if (!value) return '待上报'
      const text = String(value)
      return text.length > 22 ? `${text.slice(0, 12)}...${text.slice(-8)}` : text
    },
    resetForm() {
      this.currentStep = 1
      this.maxVisitedStep = 1
      this.reviewEditingKey = ''
      if (this.autoAdvanceTimer) {
        window.clearTimeout(this.autoAdvanceTimer)
        this.autoAdvanceTimer = null
      }
      this.robotForm = this.createEmptyForm()
      this.$nextTick(() => {
        if (this.$refs.robotFormRef) {
          this.$refs.robotFormRef.clearValidate()
        }
      })
    },
    goToStep(step) {
      if (step <= this.maxVisitedStep || step < this.currentStep) {
        if (this.autoAdvanceTimer) {
          window.clearTimeout(this.autoAdvanceTimer)
          this.autoAdvanceTimer = null
        }
        this.reviewEditingKey = ''
        this.currentStep = step
      }
    },
    stepFields(step) {
      const key = this.registerSteps[step - 1] && this.registerSteps[step - 1].key
      if (key === 'name') return ['name']
      if (key === 'category') return ['category']
      if (key === 'serviceRegionLimit') return []
      if (key === 'coverImageUrl') return ['coverImageUrl']
      if (key === 'contactPhone') return ['contactPhone']
      if (key === 'walAddress') return ['walAddress']
      return []
    },
    validateFields(fields) {
      return new Promise(resolve => {
        if (!fields.length) {
          resolve(true)
          return
        }
        const manualInvalid = fields.some(field => {
          if (field === 'name') return !this.robotForm.name
          if (field === 'category') return !this.robotForm.category
          if (field === 'coverImageUrl') return !this.robotForm.coverImageUrl
          if (field === 'contactPhone') return !this.robotForm.contactPhone
          if (field === 'walAddress') return Boolean(this.normalizeWalletAddress(this.robotForm.walAddress)) && !this.isWalletAddress(this.robotForm.walAddress)
          return false
        })
        if (manualInvalid) {
          resolve(false)
          return
        }
        resolve(true)
      })
    },
    async nextStep() {
      if (this.isReviewStep) return
      if (this.autoAdvanceTimer) {
        window.clearTimeout(this.autoAdvanceTimer)
        this.autoAdvanceTimer = null
      }
      const valid = await this.validateFields(this.stepFields(this.currentStep))
      if (!valid) {
        this.$message.warning(this.validationMessage(this.stepFields(this.currentStep)))
        return
      }
      const next = Math.min(this.currentStep + 1, this.registerSteps.length)
      this.currentStep = next
      this.maxVisitedStep = Math.max(this.maxVisitedStep, next)
      this.reviewEditingKey = ''
    },
    prevStep() {
      if (this.autoAdvanceTimer) {
        window.clearTimeout(this.autoAdvanceTimer)
        this.autoAdvanceTimer = null
      }
      this.reviewEditingKey = ''
      this.currentStep = Math.max(this.currentStep - 1, 1)
    },
    setCategory(value) {
      this.robotForm.category = value
      if (this.$refs.robotFormRef) {
        this.$refs.robotFormRef.clearValidate('category')
      }
    },
    setServiceRegionLimit(value) {
      this.robotForm.serviceRegionLimit = value
    },
    scheduleNextStep(delay = 420) {
      if (this.autoAdvanceTimer) {
        window.clearTimeout(this.autoAdvanceTimer)
      }
      this.autoAdvanceTimer = window.setTimeout(() => {
        this.autoAdvanceTimer = null
        this.nextStep()
      }, delay)
    },
    selectCategory(value) {
      this.setCategory(value)
    },
    toggleReviewEdit(key) {
      this.reviewEditingKey = this.reviewEditingKey === key ? '' : key
    },
    jumpToStepFromReview(step) {
      this.reviewEditingKey = ''
      this.goToStep(step)
    },
    async finishReviewEdit(item) {
      const valid = await this.validateFields(this.stepFields(item.step))
      if (!valid) {
        this.$message.warning('这一项还没填写完整')
        return
      }
      this.reviewEditingKey = ''
    },
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        this.$message.error('只能上传图片文件')
        return false
      }
      if (file.size > 8 * 1024 * 1024) {
        this.$message.error('图片大小不能超过 8 MB')
        return false
      }
      return true
    },
    uploadRobotImage(options) {
      const formData = new FormData()
      formData.append('file', options.file)

      const xhr = new XMLHttpRequest()
      xhr.open('POST', '/file/upload-local')
      xhr.onload = () => {
        let response = null
        try {
          response = JSON.parse(xhr.responseText || '{}')
        } catch (error) {
          options.onError(new Error('Invalid upload response'))
          return
        }
        if (xhr.status >= 200 && xhr.status < 300 && response && response.code === 200) {
          options.onSuccess(response, options.file)
          return
        }
        options.onError(new Error((response && response.msg) || `Image upload failed: ${xhr.status}`))
      }
      xhr.onerror = () => options.onError(new Error('图片上传请求失败，请确认后端服务已启动。'))
      xhr.send(formData)
    },
    handleUploadSuccess(response) {
      if (!response || response.code !== 200) {
        this.$message.error((response && response.msg) || '图片上传失败')
        return
      }
      const data = response.data || {}
      this.robotForm.coverImagePath = data.localPath || ''
      this.robotForm.coverImageUrl = data.url || ''
      if (this.$refs.robotFormRef) {
        this.$refs.robotFormRef.clearValidate('coverImageUrl')
      }
      if (this.activeQuestion.key === 'coverImageUrl') {
        this.$nextTick(() => this.scheduleNextStep(520))
      }
    },
    handleUploadError(error) {
      this.$message.error((error && error.message) || '\u56fe\u7247\u4e0a\u4f20\u5931\u8d25\uff0c\u8bf7\u91cd\u8bd5')
    },
    delay(ms) {
      return new Promise(resolve => window.setTimeout(resolve, ms))
    },
    showSubmitDialog(title, text) {
      this.submitDialogDone = false
      this.submitDialogTitle = title
      this.submitDialogText = text
      this.submitDialogVisible = true
    },
    completeSubmitDialog(title, text) {
      this.submitDialogDone = true
      this.submitDialogTitle = title
      this.submitDialogText = text
    },
    async submitRegister() {
      if (!this.currentUser || !this.currentUser.id) {
        this.$message.error('当前未识别到登录用户')
        return
      }
      if (!this.isReviewStep) {
        const valid = await this.validateFields(this.stepFields(this.currentStep))
        if (!valid) {
          this.$message.warning('请先完成当前步骤')
          return
        }
        this.currentStep = this.registerSteps.length
        this.maxVisitedStep = this.registerSteps.length
        return
      }

      const ready = await this.validateFields(['name', 'category', 'coverImageUrl', 'contactPhone', 'walAddress'])
      if (!ready) {
        this.$message.warning('清单里还有信息未完成，请点击修改补齐')
        return
      }

      const submit = async () => {
        this.showSubmitDialog('正在注册机器人', '正在提交机器人资料并进入待验证流程，请稍候。')

        try {
          const payload = {
            ownerUserId: this.currentUser.id,
            name: this.robotForm.name,
            category: this.robotForm.category,
            coverImagePath: this.robotForm.coverImagePath || this.robotForm.coverImageUrl,
            coverImageUrl: this.robotForm.coverImageUrl,
            contactPhone: this.robotForm.contactPhone,
            serviceRegionLimit: this.robotForm.serviceRegionLimit,
            walAddress: this.normalizeWalletAddress(this.robotForm.walAddress),
            capabilityDesc: this.robotForm.capabilityDesc
          }
          const response = await registerRobotProfile(payload)
          await this.delay(900)
          if (response.data.code !== 200) {
            this.submitDialogVisible = false
            this.$message.error(response.data.msg || '机器人注册失败')
            return
          }
          this.lastRegisteredRobot = {
            id: response.data.data && response.data.data.robotId,
            name: this.robotForm.name,
            category: this.robotForm.category,
            sdkBindingToken: response.data.data && response.data.data.sdkBindingToken,
            sdkBindingTokenExpiresAt: response.data.data && response.data.data.sdkBindingTokenExpiresAt
          }
          this.completeSubmitDialog('机器人注册成功', '资料已保存，当前状态为待验证通过。')
          await this.delay(900)
          this.submitDialogVisible = false
          this.resetForm()
          this.loadMyRobots()
          this.openSdkGuide(this.lastRegisteredRobot)
        } catch (error) {
          this.submitDialogVisible = false
          this.$message.error('机器人注册失败，请检查后端服务')
        }
      }

      if (this.$refs.robotFormRef) {
        this.$refs.robotFormRef.validate(valid => {
          if (valid) {
            submit()
          }
        })
        return
      }

      submit()
    },
    async loadMyRobots() {
      if (!this.currentUser || !this.currentUser.id) {
        this.myRobots = []
        return
      }

      try {
        const response = await getMyRegisteredRobots(this.currentUser.id)
        if (response.data.code === 200) {
          this.myRobots = (response.data.data || []).filter(robot => robot.registerStatus !== 'CANCELLED')
        } else {
          this.$message.error(response.data.msg || '机器人列表加载失败')
        }
      } catch (error) {
        this.$message.error('机器人列表加载失败')
      }
    },
    async regenerateToken() {
      if (!this.selectedSdkRobot || !this.currentUser || !this.currentUser.id) return
      try {
        const response = await regenerateSdkBindingToken(this.selectedSdkRobot.id, {
          userId: this.currentUser.id
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '绑定令牌生成失败')
          return
        }
        const data = response.data.data || {}
        this.selectedSdkRobot = {
          ...this.selectedSdkRobot,
          sdkBindingToken: data.sdkBindingToken,
          sdkBindingTokenExpiresAt: data.sdkBindingTokenExpiresAt,
          sdkBoundAt: null,
          sdkConfirmedAt: null,
          sdkConfirmedReportId: null,
          registerStatus: 'PENDING_REVIEW',
          verificationNote: 'SDK绑定令牌已重新生成，请在10分钟内运行安装命令。'
        }
        this.sdkStatusPollStartedAt = Date.now()
        this.sdkLastStatusKey = this.sdkProgressKey(this.selectedSdkRobot)
        this.sdkInstallSlowHintVisible = false
        this.$message.success('绑定令牌已重新生成，安装命令已更新')
        this.loadMyRobots()
      } catch (error) {
        this.$message.error('绑定令牌生成失败，请检查后端服务')
      }
    },
    async confirmDevice(robot) {
      if (!robot || !this.currentUser || !this.currentUser.id) return
      const reportId = robot.sdkLastReportId || robot.sdkConfirmedReportId || this.extractReportId(robot.verificationNote)
      if (!reportId) {
        this.$message.warning('暂未拿到SDK上报reportId，请先刷新列表或重新运行SDK')
        return
      }
      try {
        await this.$confirm(
          `确认机器人「${robot.name}」当前绑定的TPM设备就是你的设备吗？确认后后续派单会用这个公钥做DeRAS验签。`,
          '确认SDK设备',
          {
            confirmButtonText: '确认是我的设备',
            cancelButtonText: '再检查一下',
            type: 'warning'
          }
        )
        const response = await confirmSdkDevice(robot.id, {
          userId: this.currentUser.id,
          reportId
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '设备确认失败')
          return
        }
        this.$message.success('设备已确认，机器人注册完成')
        this.sdkGuideVisible = false
        this.loadMyRobots()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error('设备确认失败')
        }
      }
    },
    extractReportId(note) {
      const matched = String(note || '').match(/reportId[:：]\s*(\d+)/)
      return matched ? Number(matched[1]) : null
    },
    async cancelRobot(robot) {
      try {
        const response = await cancelRegisteredRobot({
          robotId: robot.id,
          userId: this.currentUser.id
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '取消注册失败')
          return
        }
        this.$message.success('已取消注册')
        this.loadMyRobots()
      } catch (error) {
        this.$message.error('取消注册失败，请稍后重试')
      }
    },
    categoryLabel(value) {
      const item = this.categories.find(entry => entry.value === value)
      return item ? item.label : (value || '未选择')
    }
  }
}
</script>

<style scoped>
.robot-page {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  color: #eff7ff;
  background: linear-gradient(135deg, #061120 0%, #0b1730 46%, #071528 100%);
}

.background-layer,
.glow,
.grid {
  position: absolute;
}

.background-layer {
  inset: 0;
  pointer-events: none;
}

.glow {
  border-radius: 50%;
  filter: blur(16px);
}

.glow-left {
  width: 36rem;
  height: 36rem;
  left: -10rem;
  top: -12rem;
  background: radial-gradient(circle, rgba(94, 177, 255, 0.18), transparent 68%);
}

.glow-right {
  width: 30rem;
  height: 30rem;
  right: -8rem;
  bottom: -8rem;
  background: radial-gradient(circle, rgba(255, 180, 77, 0.18), transparent 68%);
}

.grid {
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.9), transparent 96%);
}

.page-shell {
  position: relative;
  z-index: 1;
  padding: 32px 44px 44px;
}

.page-header,
.card-title-row,
.action-row,
.left-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.robot-list-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.page-header {
  justify-content: space-between;
  margin-bottom: 24px;
}

.eyebrow {
  margin: 0 0 8px;
  color: #94d4ff;
  font-size: 12px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
}

.page-header h1,
.card-title-row h3 {
  margin: 0;
  font-family: ui-serif, serif;
}

.page-header h1 {
  font-size: 42px;
}

.robot-layout,
.content-grid,
.robot-workspace {
  display: grid;
  gap: 22px;
}

.robot-workspace {
  grid-template-columns: 240px minmax(0, 1fr);
  align-items: start;
}

.robot-sidebar {
  display: grid;
  gap: 14px;
}

.robot-summary-card {
  padding: 18px;
  border-radius: 8px;
  background: rgba(7, 17, 29, 0.84);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 28px 64px rgba(0, 0, 0, 0.24);
}

.summary-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
}

.summary-head h2 {
  margin: 0;
  font-size: 22px;
}

.summary-head > span {
  flex: 0 0 auto;
  padding: 8px 10px;
  border: 1px solid rgba(142, 231, 255, 0.24);
  border-radius: 8px;
  color: #8ee7ff;
  font-weight: 800;
}

.summary-list {
  display: grid;
}

.summary-item {
  display: grid;
  grid-template-columns: 12px minmax(0, 1fr);
  gap: 10px;
  padding: 14px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.summary-item:last-child {
  border-bottom: 0;
}

.summary-item i {
  width: 8px;
  height: 8px;
  margin-top: 7px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
}

.summary-item i.ready {
  background: #74f2bf;
  box-shadow: 0 0 0 4px rgba(116, 242, 191, 0.12);
}

.summary-item span,
.summary-item strong {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.summary-item span {
  color: rgba(239, 247, 255, 0.54);
  font-size: 12px;
}

.summary-item strong {
  margin-top: 4px;
  color: #eff7ff;
  font-size: 14px;
}

.form-card,
.list-card {
  border-radius: 30px;
  padding: 28px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(18px);
  box-shadow: 0 28px 64px rgba(0, 0, 0, 0.22);
}

.sdk-output-note p,
.sdk-user-note p,
.install-step p {
  color: rgba(239, 247, 255, 0.74);
  line-height: 1.8;
}

.sdk-dialog-actions,
.robot-sdk-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.sdk-secret-panel {
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr);
  gap: 10px 12px;
  margin-top: 18px;
  padding: 16px;
  border-radius: 18px;
  background: rgba(7, 17, 29, 0.72);
  border: 1px solid rgba(155, 215, 255, 0.14);
}

.sdk-secret-panel span {
  color: rgba(239, 247, 255, 0.68);
}

.sdk-secret-panel code {
  overflow-x: auto;
  color: #ffcf8a;
  white-space: nowrap;
}

.sdk-user-note {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  margin-top: 18px;
  padding: 16px;
  border-radius: 18px;
  background: rgba(7, 17, 29, 0.72);
  border: 1px solid rgba(155, 215, 255, 0.14);
}

.sdk-user-note i {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  color: #071528;
  background: linear-gradient(135deg, #9bd7ff, #ffcf8a);
}

.sdk-user-note strong {
  display: block;
  margin-bottom: 6px;
}

.sdk-download {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.back-btn,
.ghost-btn,
.primary-btn {
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.back-btn,
.ghost-btn {
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}

.primary-btn {
  min-width: 160px;
  height: 48px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #9bd7ff, #2f86ff);
  color: #08111d;
  font-weight: 700;
  box-shadow: 0 18px 36px rgba(47, 134, 255, 0.24);
}

.back-btn:hover,
.ghost-btn:hover,
.primary-btn:hover {
  transform: translateY(-2px);
}

:deep(.process-dialog) {
  border-radius: 24px;
  background: #101b2c;
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.36);
}

:deep(.process-dialog .el-dialog__header) {
  display: none;
}

:deep(.process-dialog .el-dialog__body) {
  padding: 34px 30px;
}

.process-content {
  text-align: center;
  color: #111827;
}

.process-content i {
  display: inline-flex;
  margin-bottom: 18px;
  font-size: 48px;
}

.process-loading {
  color: #7cc8ff;
}

.process-success {
  color: #63d692;
}

.process-content h3 {
  margin: 0;
  font-size: 22px;
}

.process-content p {
  margin: 10px 0 0;
  color: #4b5563;
  line-height: 1.7;
}

.card-title-row h3 {
  font-size: 32px;
}

.form-grid {
  display: grid;
  gap: 18px;
}

.register-progress {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;
  margin: 22px 0 24px;
}

.progress-overview {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-top: 22px;
  color: rgba(239, 247, 255, 0.6);
}

.progress-overview strong {
  color: #eff7ff;
}

.progress-overview em {
  margin-left: auto;
  color: #8ee7ff;
  font-style: normal;
  font-size: 12px;
}

.step-heading {
  margin-bottom: 24px;
  padding: 22px;
  border: 1px solid rgba(142, 231, 255, 0.14);
  border-radius: 8px;
  background: rgba(6, 15, 27, 0.56);
}

.step-heading span {
  color: #ffcf8a;
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.step-heading h4 {
  margin: 10px 0 8px;
  font-size: 28px;
}

.step-heading p {
  margin: 0;
  color: rgba(239, 247, 255, 0.66);
  line-height: 1.7;
}

.flow-dot {
  min-height: 72px;
  padding: 10px 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  text-align: center;
  color: #eff7ff;
  background: rgba(8, 14, 25, 0.58);
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.flow-dot span {
  width: 30px;
  height: 30px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #08111d;
  font-weight: 800;
  background: rgba(255, 255, 255, 0.72);
}

.flow-dot em {
  color: rgba(239, 247, 255, 0.62);
  font-style: normal;
  font-size: 12px;
  line-height: 1.2;
}

.flow-dot.active {
  border-color: rgba(155, 215, 255, 0.56);
  background: rgba(155, 215, 255, 0.12);
  transform: translateY(-2px);
}

.flow-dot.active span,
.flow-dot.done span {
  background: linear-gradient(135deg, #9bd7ff, #2f86ff);
}

.flow-dot.done {
  border-color: rgba(99, 214, 146, 0.28);
}

.flow-dot.locked {
  opacity: 0.42;
  cursor: not-allowed;
}

.flow-dot.locked:hover {
  transform: none;
}

.question-panel {
  min-height: 430px;
}

.single-field {
  max-width: 760px;
}

.choice-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}


.category-choice-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.category-choice-head strong {
  font-size: 13px;
  letter-spacing: 0;
  text-transform: uppercase;
}

.category-choice-head span {
  color: rgba(239, 247, 255, 0.58);
  font-size: 13px;
}

.category-choice-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.wallet-guide {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 12px;
}

.region-limit-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: -2px;
  color: rgba(226, 237, 248, 0.66);
  font-size: 13px;
  line-height: 1.6;
}

.region-limit-actions button {
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid rgba(142, 231, 255, 0.22);
  border-radius: 8px;
  color: #a9eaff;
  background: rgba(142, 231, 255, 0.07);
  cursor: pointer;
}

.region-limit-actions button:hover {
  border-color: rgba(142, 231, 255, 0.48);
  background: rgba(142, 231, 255, 0.13);
}

.wallet-option {
  padding: 16px;
  border-radius: 18px;
  background: rgba(8, 14, 25, 0.62);
  border: 1px solid rgba(155, 215, 255, 0.16);
}

.wallet-option span {
  display: inline-flex;
  margin-bottom: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  color: #071528;
  font-size: 12px;
  font-weight: 800;
  background: #9bd7ff;
}

.wallet-option p {
  margin: 0;
  color: rgba(239, 247, 255, 0.72);
  line-height: 1.7;
}

.choice-form-item :deep(.el-form-item__content) {
  display: block;
  width: 100%;
  line-height: normal;
}

.choice-card {
  min-height: 154px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 22px;
  color: #eff7ff;
  text-align: left;
  background: rgba(8, 14, 25, 0.64);
  cursor: pointer;
  transition: transform 0.22s ease, border-color 0.22s ease, background 0.22s ease;
}

.choice-card:hover,
.choice-card.selected {
  transform: translateY(-3px);
  border-color: rgba(155, 215, 255, 0.55);
  background: rgba(155, 215, 255, 0.12);
}

.choice-card span {
  width: 46px;
  height: 46px;
  margin-bottom: 14px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #08111d;
  font-weight: 800;
  background: linear-gradient(135deg, #9bd7ff, #2f86ff);
}

.choice-card strong {
  display: block;
  margin-bottom: 8px;
  font-size: 20px;
}

.choice-card p {
  margin: 0;
  color: rgba(239, 247, 255, 0.68);
  line-height: 1.7;
}

.choice-card small {
  display: inline-flex;
  margin-top: 14px;
  padding: 6px 10px;
  border-radius: 999px;
  color: #071528;
  font-size: 12px;
  font-weight: 700;
  background: #9bd7ff;
}

.two-col {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.full-width {
  width: 100%;
}

.robot-form :deep(.el-form-item__label) {
  color: #cfe8ff;
  padding-bottom: 6px;
}

.robot-form :deep(.el-input__inner),
.robot-form :deep(.el-textarea__inner),
.robot-form :deep(.el-select .el-input__inner) {
  color: #fff;
  background: rgba(8, 14, 25, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;
}

.robot-upload {
  width: 100%;
}

.upload-placeholder,
.upload-preview {
  min-height: 220px;
  border-radius: 24px;
  border: 1px dashed rgba(255, 255, 255, 0.18);
  background: rgba(8, 14, 25, 0.68);
  display: grid;
  place-items: center;
  color: #d8ebff;
  overflow: hidden;
}

.upload-placeholder i {
  font-size: 28px;
  margin-bottom: 10px;
}

.upload-preview img {
  width: 100%;
  height: 220px;
  object-fit: cover;
}

.upload-preview span {
  padding: 12px 14px;
}

.compact-preview {
  min-height: 160px;
}

.compact-preview img {
  height: 160px;
}

.final-review {
  display: grid;
  gap: 18px;
}

.review-head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 320px) auto;
  align-items: center;
  gap: 16px;
  padding: 18px;
  border-radius: 20px;
  background: rgba(8, 14, 25, 0.58);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.review-head span {
  display: block;
  margin-bottom: 6px;
  color: #9bd7ff;
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.review-head h4 {
  margin: 0;
  font-size: 24px;
}

.review-head p {
  max-width: 320px;
  margin: 0;
  color: rgba(239, 247, 255, 0.68);
  line-height: 1.7;
}

.review-meter {
  min-width: 92px;
  padding: 12px 14px;
  border-radius: 16px;
  text-align: center;
  background: rgba(155, 215, 255, 0.12);
  border: 1px solid rgba(155, 215, 255, 0.22);
}

.review-meter strong {
  display: block;
  color: #9bd7ff;
  font-size: 22px;
}

.review-meter em {
  color: rgba(239, 247, 255, 0.68);
  font-style: normal;
  font-size: 12px;
}

.review-list {
  display: grid;
  gap: 12px;
}

.review-item {
  display: grid;
  padding: 16px 18px;
  border-radius: 18px;
  background: rgba(8, 14, 25, 0.62);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: border-color 0.22s ease, background 0.22s ease;
}

.review-item.editing {
  border-color: rgba(155, 215, 255, 0.42);
  background: rgba(155, 215, 255, 0.08);
}

.review-item.incomplete {
  border-color: rgba(255, 122, 122, 0.34);
}

.review-main {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
}

.review-index {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  color: #08111d;
  font-weight: 800;
  background: linear-gradient(135deg, #9bd7ff, #2f86ff);
}

.review-item span {
  display: block;
  margin-bottom: 6px;
  color: rgba(239, 247, 255, 0.58);
  font-size: 12px;
}

.review-item strong {
  display: block;
  color: #fff;
  line-height: 1.6;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.review-item button {
  height: 36px;
  padding: 0 14px;
  border: 1px solid rgba(155, 215, 255, 0.28);
  border-radius: 999px;
  color: #d8ebff;
  background: rgba(155, 215, 255, 0.1);
  cursor: pointer;
}

.review-editor {
  margin-top: 16px;
  padding: 16px;
  border-radius: 16px;
  background: rgba(8, 14, 25, 0.58);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.review-choice-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 12px;
}

.mini-choice {
  min-height: 74px;
  padding: 12px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #eff7ff;
  text-align: left;
  background: rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.mini-choice:hover,
.mini-choice.selected {
  transform: translateY(-2px);
  border-color: rgba(155, 215, 255, 0.48);
  background: rgba(155, 215, 255, 0.12);
}

.mini-choice span {
  width: 30px;
  height: 30px;
  margin-bottom: 8px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #08111d;
  font-weight: 800;
  background: linear-gradient(135deg, #9bd7ff, #2f86ff);
}

.mini-choice strong {
  font-size: 15px;
}

.review-field-grid {
  display: grid;
  gap: 12px;
}

.review-editor-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.review-done-btn,
.review-step-btn {
  height: 38px;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid rgba(155, 215, 255, 0.28);
  cursor: pointer;
}

.review-done-btn {
  color: #08111d;
  font-weight: 700;
  background: linear-gradient(135deg, #9bd7ff, #2f86ff);
}

.review-step-btn {
  color: #d8ebff;
  background: rgba(155, 215, 255, 0.1);
}

.wizard-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.question-slide-enter-active,
.question-slide-leave-active,
.review-editor-enter-active,
.review-editor-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.question-slide-enter,
.question-slide-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

.review-editor-enter,
.review-editor-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.action-row {
  margin-top: 8px;
}

.robot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px;
}

.robot-card {
  overflow: hidden;
  border-radius: 24px;
  background: rgba(8, 14, 25, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.robot-card-cancelled {
  opacity: 0.8;
  border-color: rgba(255, 152, 152, 0.22);
  box-shadow: inset 0 0 0 1px rgba(255, 152, 152, 0.08);
}

.robot-cover {
  position: relative;
  height: 220px;
}

.robot-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.robot-card-cancelled .robot-cover img {
  filter: grayscale(0.92) brightness(0.72);
}

.robot-cover-overlay {
  position: absolute;
  top: 14px;
  right: 14px;
}

.cancel-ribbon {
  position: absolute;
  left: -34px;
  top: 20px;
  width: 150px;
  padding: 8px 0;
  text-align: center;
  transform: rotate(-35deg);
  background: linear-gradient(135deg, rgba(255, 132, 132, 0.92), rgba(255, 94, 94, 0.92));
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  box-shadow: 0 10px 22px rgba(255, 94, 94, 0.22);
}

.robot-card-body {
  padding: 18px;
}

.robot-sdk-actions {
  margin-top: 14px;
}

.sdk-mini-btn {
  flex: 1 1 120px;
  height: 38px;
  border: 1px solid rgba(155, 215, 255, 0.2);
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #d8ebff;
  text-decoration: none;
  background: rgba(155, 215, 255, 0.1);
  cursor: pointer;
}

.sdk-mini-btn.confirm {
  color: #06111e;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
}

.robot-card-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
}

.robot-card-head h4 {
  margin: 0;
  font-size: 20px;
  color: #f5fbff;
}

.robot-card-head span,
.robot-contact,
.robot-wallet,
.robot-desc,
.robot-note,
.empty-state {
  color: rgba(239, 247, 255, 0.76);
}

.robot-desc,
.robot-note,
.robot-contact,
.robot-wallet {
  margin: 10px 0 0;
  line-height: 1.75;
}

.robot-note.warning {
  color: #ffe1a6;
  font-weight: 800;
}

.robot-token-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid rgba(155, 215, 255, 0.18);
  background: rgba(155, 215, 255, 0.08);
  color: rgba(239, 247, 255, 0.78);
  font-size: 12px;
}

.robot-token-line span {
  min-width: 0;
  word-break: break-all;
}

.robot-token-line button {
  flex: 0 0 auto;
  height: 30px;
  padding: 0 10px;
  border: none;
  border-radius: 999px;
  color: #071528;
  font-size: 12px;
  font-weight: 700;
  background: #9bd7ff;
  cursor: pointer;
}

.robot-agent-line {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid rgba(155, 215, 255, 0.14);
  background: rgba(155, 215, 255, 0.08);
  color: rgba(239, 247, 255, 0.78);
  font-size: 12px;
}

.robot-agent-line span {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #a8b4c3;
  box-shadow: 0 0 0 4px rgba(168, 180, 195, 0.12);
}

.robot-agent-line strong {
  color: #f5fbff;
}

.robot-agent-line em {
  margin-left: auto;
  color: rgba(239, 247, 255, 0.58);
  font-style: normal;
}

.robot-agent-line.online {
  border-color: rgba(143, 241, 199, 0.24);
  background: rgba(143, 241, 199, 0.1);
}

.robot-agent-line.online span {
  background: #8ff1c7;
  box-shadow: 0 0 0 4px rgba(143, 241, 199, 0.16);
}

.robot-agent-line.offline span {
  background: #ffb36b;
  box-shadow: 0 0 0 4px rgba(255, 179, 107, 0.14);
}

.dispatch-toggle-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  padding: 12px;
  border-radius: 16px;
  border: 1px solid rgba(255, 208, 138, 0.18);
  background: rgba(255, 208, 138, 0.08);
}

.dispatch-toggle-line.accepting {
  border-color: rgba(143, 241, 199, 0.24);
  background: rgba(143, 241, 199, 0.1);
}

.dispatch-toggle-line strong,
.dispatch-toggle-line span {
  display: block;
}

.dispatch-toggle-line strong {
  color: #f5fbff;
}

.dispatch-toggle-line span {
  margin-top: 4px;
  color: rgba(239, 247, 255, 0.58);
  font-size: 12px;
}

.dispatch-toggle-line button {
  min-width: 72px;
  height: 34px;
  border: none;
  border-radius: 999px;
  color: #06111e;
  font-weight: 800;
  cursor: pointer;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
}

.dispatch-toggle-line button:disabled {
  cursor: not-allowed;
  color: rgba(245, 251, 255, 0.5);
  background: rgba(255, 255, 255, 0.08);
}

.device-check-card {
  margin-top: 12px;
  padding: 12px;
  border-radius: 16px;
  border: 1px solid rgba(143, 241, 199, 0.14);
  background: rgba(4, 10, 19, 0.38);
}

.device-check-head,
.sdk-device-check-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.device-check-head strong,
.sdk-device-check-title h4 {
  margin: 0;
  color: #f5fbff;
}

.device-check-head em,
.sdk-device-check-title > strong {
  color: rgba(143, 241, 199, 0.86);
  font-size: 12px;
  font-style: normal;
}

.device-check-steps {
  display: grid;
  gap: 8px;
  margin-top: 10px;
}

.device-check-steps.dialog {
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
}

.device-check-step {
  min-width: 0;
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 9px;
  align-items: start;
  padding: 10px;
  border-radius: 14px;
  border: 1px solid rgba(155, 215, 255, 0.12);
  background: rgba(155, 215, 255, 0.06);
}

.device-check-step > span {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: rgba(239, 247, 255, 0.72);
  background: rgba(155, 215, 255, 0.12);
}

.device-check-step strong {
  display: block;
  color: rgba(245, 251, 255, 0.9);
  font-size: 12px;
}

.device-check-step small {
  display: block;
  margin-top: 4px;
  color: rgba(239, 247, 255, 0.58);
  line-height: 1.45;
  word-break: break-all;
}

.device-check-step.done {
  border-color: rgba(143, 241, 199, 0.24);
  background: rgba(143, 241, 199, 0.09);
}

.device-check-step.done > span {
  color: #071528;
  background: #8ff1c7;
}

.device-check-step.current {
  border-color: rgba(255, 208, 138, 0.28);
  background: rgba(255, 208, 138, 0.1);
}

.device-check-step.current > span {
  color: #071528;
  background: #ffd08a;
}

.device-check-step.failed {
  border-color: rgba(255, 130, 130, 0.3);
  background: rgba(255, 130, 130, 0.11);
}

.device-check-step.failed > span {
  color: #fff;
  background: #ff7f7f;
}

.robot-card-cancelled .robot-card-head h4,
.robot-card-cancelled .robot-card-head span,
.robot-card-cancelled .robot-contact,
.robot-card-cancelled .robot-wallet,
.robot-card-cancelled .robot-desc,
.robot-card-cancelled .robot-note {
  color: rgba(239, 247, 255, 0.58);
}

.cancel-btn {
  width: 100%;
  margin-top: 16px;
  height: 42px;
  border: 1px solid rgba(255, 141, 118, 0.26);
  border-radius: 999px;
  background: rgba(255, 141, 118, 0.12);
  color: #ffc4b1;
  cursor: pointer;
  transition: transform 0.22s ease, background 0.22s ease, opacity 0.22s ease;
}

.cancel-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  background: rgba(255, 141, 118, 0.18);
}

.cancel-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.empty-state {
  padding: 22px;
  border-radius: 22px;
  background: rgba(8, 14, 25, 0.68);
  border: 1px solid rgba(255, 255, 255, 0.08);
  text-align: center;
}

:deep(.sdk-dialog) {
  width: min(860px, calc(100vw - 32px)) !important;
  max-height: calc(100vh - 32px);
  border-radius: 26px;
  overflow: hidden;
  background: #0d1929;
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.4);
}

:deep(.sdk-dialog .el-dialog__header) {
  display: none;
}

:deep(.sdk-dialog .el-dialog__body) {
  padding: 0;
}

.sdk-dialog-content {
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  padding: 28px;
  color: var(--roc-text);
}

.sdk-dialog-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 108px;
  gap: 20px;
  align-items: center;
  padding: 22px;
  border-radius: 22px;
  background: linear-gradient(135deg, rgba(142, 231, 255, 0.12), rgba(255, 207, 138, 0.08));
  border: 1px solid rgba(142, 231, 255, 0.22);
}

.sdk-dialog-hero h3 {
  margin: 0 0 10px;
  font-size: 30px;
  overflow-wrap: anywhere;
}

.dialog-chip {
  width: 96px;
  height: 96px;
  border-radius: 24px;
  display: grid;
  place-items: center;
  color: #071528;
  font-weight: 800;
  background: linear-gradient(135deg, #9bd7ff, #ffcf8a);
}

.selected-robot {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin: 18px 0;
  padding: 14px 16px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid var(--roc-border);
}

.selected-robot span,
.selected-robot em {
  color: var(--roc-muted);
  font-style: normal;
}

.sdk-device-check {
  margin: 18px 0;
  padding: 18px;
  border-radius: 20px;
  border: 1px solid rgba(143, 241, 199, 0.16);
  background:
    linear-gradient(135deg, rgba(143, 241, 199, 0.08), rgba(155, 215, 255, 0.05)),
    rgba(255, 255, 255, 0.05);
}

.sdk-slow-hint {
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  margin: 14px 0 18px;
  padding: 14px 16px;
  border-radius: 18px;
  border: 1px solid rgba(255, 207, 138, 0.24);
  background: rgba(255, 207, 138, 0.09);
}

.sdk-slow-hint i {
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  color: #071528;
  background: #ffcf8a;
  font-size: 20px;
}

.sdk-slow-hint strong {
  display: block;
  color: #fff4df;
}

.sdk-slow-hint p {
  margin: 6px 0 0;
  color: rgba(239, 247, 255, 0.72);
  line-height: 1.6;
}

.install-steps {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 18px;
}

.install-step {
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr);
  gap: 12px;
  padding: 16px;
  border-radius: 18px;
  background: rgba(8, 14, 25, 0.68);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.install-step span {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  color: #071528;
  font-weight: 800;
  background: linear-gradient(135deg, #9bd7ff, #2f86ff);
}

.install-step strong {
  display: block;
  margin-bottom: 6px;
}

.command-panel {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  margin-top: 18px;
  padding: 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid var(--roc-border);
}

.command-panel button {
  max-width: 100%;
  white-space: normal;
  line-height: 1.35;
}

.command-panel span {
  display: block;
  margin-bottom: 8px;
  color: var(--roc-muted);
}

.command-block {
  min-width: 0;
}

.command-panel code {
  display: block;
  overflow-x: auto;
  padding: 10px 12px;
  border-radius: 8px;
  color: #dff8ff;
  background: rgba(2, 10, 18, 0.82);
  white-space: normal;
  word-break: break-all;
}

.copy-btn {
  height: 40px;
  padding: 0 14px;
  border: none;
  border-radius: 999px;
  color: #071528;
  font-weight: 700;
  background: #ffcf8a;
  cursor: pointer;
}

.copy-btn.secondary {
  background: linear-gradient(135deg, #9bd7ff, #8ff1c7);
}

.sdk-confirm-box {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: center;
  margin-top: 16px;
  padding: 16px;
  border-radius: 18px;
  border: 1px solid rgba(143, 241, 199, 0.22);
  background: rgba(143, 241, 199, 0.1);
}

.sdk-confirm-box p {
  margin: 6px 0 0;
  color: rgba(239, 247, 255, 0.74);
  line-height: 1.6;
}

.sdk-output-note {
  margin-top: 18px;
  padding: 16px;
  border-radius: 18px;
  background: rgba(255, 207, 138, 0.08);
  border: 1px solid rgba(255, 207, 138, 0.16);
}

.sdk-output-note strong {
  display: block;
  margin-bottom: 8px;
}

.sdk-dialog-actions {
  justify-content: flex-end;
  margin-top: 20px;
}

.wallet-defer-note,
.field-hint {
  display: block;
  margin: -4px 0 0;
  color: rgba(226, 237, 248, 0.66);
  font-size: 13px;
  line-height: 1.65;
}

.sdk-mini-btn.wallet {
  border-color: rgba(255, 207, 138, 0.34);
  color: #ffe1a6;
  background: rgba(255, 207, 138, 0.1);
}

.sdk-dialog-content .sdk-device-check,
.sdk-dialog-content .sdk-slow-hint,
.sdk-dialog-content .install-step,
.sdk-dialog-content .sdk-user-note,
.sdk-dialog-content .sdk-output-note,
.sdk-dialog-content .sdk-confirm-box {
  color: var(--roc-text);
  border-color: var(--roc-border);
  background: rgba(255, 255, 255, 0.045);
}

.sdk-dialog-content .sdk-device-check h4,
.sdk-dialog-content .device-check-step strong,
.sdk-dialog-content .sdk-user-note strong,
.sdk-dialog-content .sdk-output-note strong,
.sdk-dialog-content .sdk-confirm-box strong,
.sdk-dialog-content .install-step strong {
  color: var(--roc-text);
}

.sdk-dialog-content .sdk-slow-hint p,
.sdk-dialog-content .install-step p,
.sdk-dialog-content .sdk-user-note p,
.sdk-dialog-content .sdk-output-note p,
.sdk-dialog-content .sdk-confirm-box p,
.sdk-dialog-content .device-check-step small {
  color: var(--roc-muted);
}

.reveal-up {
  animation: revealUp 0.8s ease both;
}

.delay-1 {
  animation-delay: 0.1s;
}

.delay-2 {
  animation-delay: 0.2s;
}

.delay-3 {
  animation-delay: 0.3s;
}

@keyframes revealUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1080px) {
  .robot-workspace,
  .content-grid,
  .two-col,
  .install-steps,
  .device-check-steps.dialog,
  .sdk-dialog-hero,
  .command-panel,
  .review-head {
    grid-template-columns: 1fr;
  }

  .device-check-head,
  .sdk-device-check-title {
    align-items: flex-start;
    flex-direction: column;
  }

  .register-progress {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .category-choice-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.choice-card.selected {
  border: 2px solid #74e7c2;
  background: rgba(63, 187, 153, 0.18);
  box-shadow: 0 0 0 3px rgba(116, 231, 194, 0.16), 0 18px 38px rgba(26, 213, 177, 0.14);
}

.choice-card.selected small {
  color: #06130f;
  background: #74e7c2;
}

@media (max-width: 768px) {
  .robot-sidebar {
    display: none;
  }

  .progress-overview {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .progress-overview em {
    width: 100%;
    margin-left: 0;
  }

  .page-shell {
    padding: 20px 16px 28px;
  }

  .page-header,
  .card-title-row,
  .action-row,
  .wizard-actions {
    flex-direction: column;
    align-items: flex-start;
  }

  .left-actions {
    flex-wrap: wrap;
  }

  .page-header h1 {
    font-size: 34px;
  }

  .sdk-secret-panel {
    grid-template-columns: 1fr;
  }

  .register-progress {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .review-main,
  .review-choice-row,
  .wallet-guide {
    grid-template-columns: 1fr;
  }

  .region-limit-actions {
    align-items: flex-start;
    flex-direction: column;
  }

  .review-index {
    width: 38px;
    height: 38px;
  }

  .form-card,
  .list-card {
    padding: 20px;
    border-radius: 24px;
  }

  :deep(.sdk-dialog) {
    width: calc(100vw - 20px) !important;
    max-height: calc(100vh - 20px);
  }

  .sdk-dialog-content {
    max-height: calc(100vh - 20px);
    padding: 18px;
  }

  .sdk-dialog-hero {
    padding: 16px;
  }

  .sdk-dialog-hero h3 {
    font-size: 25px;
  }

  .dialog-chip {
    width: 72px;
    height: 72px;
  }

  .category-choice-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }

  .category-choice-grid {
    grid-template-columns: 1fr;
  }
}
</style>
